/**
 * Discharge Module Constants
 * Discharge limits, status codes, messages, and configuration
 */

// Discharge limits (from backend DischargeController)
export const DISCHARGE_LIMITS = {
  DEFAULT: 50000,
  FORFEITURE_DEFAULT: 10000,
  FORFEITURE_NC_TX: 0, // Cannot discharge in forfeiture
};

export const DISCHARGE_VALIDATION_STATUS = {
  READY: 'ready',
  NEEDS_PROOF: 'needsProof',
  BLOCKED: 'blocked',
};

// States with special forfeiture rules
export const NO_FORFEITURE_DISCHARGE_STATES = ['NC', 'TX'];

// States with raised discharge thresholds (from backend DischargeController).
// Listing a state here is all that is needed for it to follow the raised limits.
export const SPECIAL_LIMIT_STATES = {
  CA: { normal: 100000, forfeiture: 25000 },
  CT: { normal: 100000, forfeiture: 25000 },
};

// Status codes that block discharge
export const BLOCKED_STATUSES = {
  DISCHARGED: 'D',
  UNEXECUTED: 'U',
  READ_ONLY: 'RO',
};

// User-facing messages (aligned with backend proofReason values)
export const DISCHARGE_MESSAGES = {
  ALREADY_DISCHARGED: 'This power has already been discharged.',
  UNEXECUTED: 'Unexecuted powers cannot be discharged.',
  JUDGMENT: 'Powers in judgment status require proof of discharge.',
  TRANSFER_BOND: 'Transfer bond powers require proof of discharge.',
  NC_TX_FORFEITURE: 'Forfeiture powers in NC/TX require proof of discharge.',
  FORFEITURE_OVER_LIMIT: 'Forfeiture powers > $10,000 require proof.',
  OVER_LIMIT: 'Bonds > $50,000 require proof.',
  NO_FORFEITURE_STATE: 'Forfeiture powers cannot be discharged online in this state.',
  OPEN_BATCH: 'This power needs to be processed further before it can be discharged.',
  PENDING_REVIEW:
    'This power will be reviewed over the next 2 business days and discharged if the proof is sufficient.',
  MAX_REACHED: 'Maximum number of powers reached for this discharge transaction.',
};

// Proof messages for states in SPECIAL_LIMIT_STATES. Built per state so adding a
// state needs no new copy. Wording must stay aligned with backend proofReason values.
const formatLimit = (amount) => `$${amount.toLocaleString('en-US')}`;

export const buildStateOverLimitMessage = (state, limit) =>
  `Bonds in ${state} > ${formatLimit(limit)} require proof.`;

export const buildStateForfeitureOverLimitMessage = (state, limit) =>
  `Forfeiture powers in ${state} > ${formatLimit(limit)} require proof.`;

// API error codes
export const DISCHARGE_ERROR_CODES = {
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  PROOF_REQUIRED: 'PROOF_REQUIRED',
};

// User-friendly error code descriptions
export const ERROR_CODE_ACTIONS = {
  NOT_FOUND: 'Verify power number',
  VALIDATION_FAILED: 'Check validation errors',
  PROOF_REQUIRED: 'Upload proof before discharge',
};

export const PROOF_FILE_CONFIG = {
  MAX_SIZE_MB: 10,
  MAX_SIZE_BYTES: 10 * 1024 * 1024,
  ACCEPTED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/tiff', 'application/pdf'],
  ACCEPTED_EXTENSIONS: '.jpg,.jpeg,.png,.gif,.tiff,.tif,.pdf',
};

export const DISCHARGE_TABS = {
  DISCHARGE: 0,
  PENDING: 1,
  COMPLETED: 2,
};

// Status strings that map to each history tab
export const HISTORY_TAB_STATUSES = {
  [1]: ['Pending Review', 'Failed'],
  [2]: ['Completed'],
};

// Validation status labels for inline display
export const VALIDATION_STATUS_LABELS = {
  ready: 'Ready',
  needsProof: 'Proof Required',
  blocked: 'Blocked',
};

// Power status colors (matching ResultsTable.jsx)
export const STATUS_COLORS = {
  'E': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  'U': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  'D': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  'F': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  'J': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  'T': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  'V': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  'R': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
};

// Validation status colors
export const VALIDATION_COLORS = {
  ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  needsProof: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

// Status filter options for PowerSelectionTable
export const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'E', label: 'Executed' },
  { key: 'F', label: 'Forfeiture' },
  { key: 'J', label: 'Judgment' },
  { key: 'T', label: 'Transfer' },
];

// Status filter options for DischargeHistoryTable
export const HISTORY_STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'Completed', label: 'Completed' },
  { key: 'Pending Review', label: 'Pending Review' },
  { key: 'Failed', label: 'Failed' },
];

// Discharge history status styles
export const HISTORY_STATUS_COLORS = {
  'Completed': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  'Pending Review': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  'Failed': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

// Draft status display colors
export const DRAFT_STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  'Submitted': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};
